import {useEffect,useRef} from 'react';

/** The host may replace the browser widget, for example with a native WebView bridge. */
export type CaptchaChallengeProps={siteKey:string;onToken:(token:string|null)=>void;onError:(message:string)=>void;resetKey:number};
type TurnstileApi={render:(container:HTMLElement,options:Record<string,unknown>)=>string;remove:(id:string)=>void};
declare global { interface Window {turnstile?:TurnstileApi} }
const SCRIPT_URL='https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptPromise:Promise<TurnstileApi>|undefined;
function loadTurnstile():Promise<TurnstileApi>{
  if(window.turnstile)return Promise.resolve(window.turnstile);
  if(scriptPromise)return scriptPromise;
  scriptPromise=new Promise<TurnstileApi>((resolve,reject)=>{
    const script=document.createElement('script');script.src=SCRIPT_URL;script.async=true;script.defer=true;
    let settled=false;
    const fail=()=>{if(settled)return;settled=true;window.clearTimeout(timeout);script.onload=null;script.onerror=null;script.remove();reject(new Error('Security check could not load. Please try again.'));};
    const timeout=window.setTimeout(fail,15_000);
    script.onload=()=>{if(settled)return;if(!window.turnstile){fail();return;}settled=true;window.clearTimeout(timeout);resolve(window.turnstile);};
    script.onerror=fail;
    document.head.appendChild(script);
  }).catch(error=>{scriptPromise=undefined;throw error;});
  return scriptPromise;
}

export function TurnstileChallenge({siteKey,onToken,onError,resetKey}:CaptchaChallengeProps){
  const container=useRef<HTMLDivElement>(null),callbacks=useRef({onToken,onError});callbacks.current={onToken,onError};
  useEffect(()=>{
    let active=true,id:string|undefined,api:TurnstileApi|undefined;
    callbacks.current.onToken(null);
    void loadTurnstile().then(instance=>{
      if(!active||!container.current)return;
      api=instance;
      try{id=instance.render(container.current,{sitekey:siteKey,action:'sign-in',appearance:'interaction-only',size:'flexible',callback:(token:string)=>{if(active)callbacks.current.onToken(token);},'expired-callback':()=>{if(active){callbacks.current.onToken(null);callbacks.current.onError('Security check expired. Please try again.');}},'timeout-callback':()=>{if(active){callbacks.current.onToken(null);callbacks.current.onError('Security check timed out. Please try again.');}},'error-callback':()=>{if(active){callbacks.current.onToken(null);callbacks.current.onError('Security check could not be completed. Please try again.');}return true;}});}catch{callbacks.current.onError('Security check could not be loaded. Please try again.');}
    }).catch(()=>{if(active)callbacks.current.onError('Security check could not load. Please try again.');});
    return()=>{active=false;if(id&&api)api.remove(id);};
  },[siteKey,resetKey]);
  return <div ref={container} className="rr-turnstile" aria-label="Sign-in security check"/>;
}
